import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Tag } from 'src/app/data/model/tag';

export interface TagPillItem {
	label: string;
	backColor?: string;
	textColor?: string;
	/** Colour of the leading dot. The pill itself stays white. */
	dotColor?: string;
	tagId?: string;
	invoiceId?: string;
}

/** Used when a tag carries no colour of its own. */
const DEFAULT_DOT_COLOR = '#6c757d';

@Component({
	selector: 'app-tag-pill',
	templateUrl: './tag-pill.component.html',
	styleUrls: ['./tag-pill.component.scss']
})
export class TagPillComponent implements OnChanges {
	/** Raw tags coming from API */
	@Input() tags: Array<Tag | TagPillItem> = [];
	/** Maximum pills to display before truncating */
	@Input() maxVisible: number | null = null;
	/** Display curated placeholders when no tags exist */
	@Input() showPlaceholderWhenEmpty: boolean = false;
	@Input() placeholderTags: TagPillItem[] = [
		{ label: 'Utilities', backColor: '#EEF2FF', textColor: '#1E1B4B' },
		{ label: 'Payroll', backColor: '#E0F7F4', textColor: '#064E3B' },
		{ label: 'Subscriptions', backColor: '#FFF7ED', textColor: '#92400E' }
	];
	/** Visual density for pills */
	@Input() pillSize: 'sm' | 'md' = 'md';
	/** Emit when a pill is cleared */
	@Output() pillCleared = new EventEmitter<TagPillItem>();
	@Output() pillClicked = new EventEmitter<TagPillItem>();
	private dismissedTagLabels = new Set<string>();

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['tags'] || changes['placeholderTags']) {
			this.dismissedTagLabels.clear();
		}
	}

	get displayTags(): TagPillItem[] {
		const hasRealTags = Array.isArray(this.tags) && this.tags.length > 0;
		const source = hasRealTags
			? this.tags
			: this.showPlaceholderWhenEmpty
				? this.placeholderTags
				: [];

		const normalized = source
			.filter(Boolean)
			.map(tag => this.normalizeTag(tag))
			.filter(tag => !this.dismissedTagLabels.has(tag.label));

		if (typeof this.maxVisible === 'number' && this.maxVisible >= 0) {
			return normalized.slice(0, this.maxVisible);
		}

		return normalized;
	}

	private normalizeTag(tag: Tag | TagPillItem): TagPillItem {
		if (!tag) {
			return { label: '', backColor: '#EEF2FF', textColor: '#111827', dotColor: DEFAULT_DOT_COLOR };
		}

		if ((tag as Tag).tagName !== undefined) {
			const casted = tag as Tag;
			return {
				label: casted.tagName,
				backColor: casted.backColor || '#EEF2FF',
				textColor: casted.textColor || '#111827',
				dotColor: this.dotColorOf(casted.textColor, casted.backColor),
				tagId: casted.tagId,
				invoiceId: casted.invoiceId
			};
		}

		const pill = tag as TagPillItem;
		return {
			label: pill.label,
			backColor: pill.backColor || '#EEF2FF',
			textColor: pill.textColor || '#111827',
			dotColor: pill.dotColor || this.dotColorOf(pill.textColor, pill.backColor),
			tagId: pill.tagId,
			invoiceId: pill.invoiceId
		};
	}

	/**
	 * Tags store a pale `backColor` with a saturated `textColor` on top. The dot
	 * wants the saturated one, since the pill behind it is now always white.
	 */
	private dotColorOf(textColor?: string, backColor?: string): string {
		return textColor || backColor || DEFAULT_DOT_COLOR;
	}

	clearTag(tag: TagPillItem, event: MouseEvent): void {
		event.stopPropagation();
		this.dismissedTagLabels.add(tag.label);
		this.pillCleared.emit(tag);
	}
}
